class ChillersFilters {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.debounceTimer = null;
    this.controller = null;
    this.filtersSnapshot = '';
    this.lastFocus = null;
    this.pendingFocus = null;

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.root.addEventListener('change', this.onChange);
    this.root.addEventListener('submit', this.onSubmit);
    this.root.addEventListener('click', this.onClick);

    this.desktopQuery = window.matchMedia('(min-width: 990px)');
    this.onModeChange = this.onModeChange.bind(this);
    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onModeChange);
    } else if (this.desktopQuery.addListener) {
      this.desktopQuery.addListener(this.onModeChange);
    }

    this.syncMode();
    this.decorateFromUrl();
  }

  get form() {
    return this.root.querySelector('[data-chillers-form]');
  }

  get drawer() {
    return this.root.querySelector('[data-chillers-drawer]');
  }

  get isDesktop() {
    return this.desktopQuery.matches;
  }

  onModeChange() {
    if (!document.contains(this.root)) {
      if (this.desktopQuery.removeEventListener) {
        this.desktopQuery.removeEventListener('change', this.onModeChange);
      }
      return;
    }

    if (this.isDesktop && this.root.classList.contains('is-drawer-open')) {
      this.closeDrawer({ discard: true });
    }
    this.syncMode();
  }

  syncMode() {
    const drawer = this.drawer;
    if (!drawer) return;

    if (this.isDesktop) {
      this.root.classList.remove('is-drawer-open');
      document.body.classList.remove('chillers-filters-lock');
      drawer.removeAttribute('inert');
      drawer.removeAttribute('aria-hidden');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      return;
    }

    if (!this.root.classList.contains('is-drawer-open')) {
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
    }
  }

  onChange(event) {
    const target = event.target;

    if (target.matches('[data-chillers-all]')) {
      this.form.querySelectorAll('[data-chillers-category]').forEach((input) => {
        input.checked = false;
      });
      target.checked = true;
      if (this.isDesktop) this.submitSoon(0);
      return;
    }

    if (target.matches('[data-chillers-category]')) {
      const anyChecked = Array.from(this.form.querySelectorAll('[data-chillers-category]')).some(
        (input) => input.checked
      );
      const allProducts = this.form.querySelector('[data-chillers-all]');
      if (allProducts) allProducts.checked = !anyChecked;
      if (this.isDesktop) this.submitSoon(0);
      return;
    }

    if (target.matches('[data-chillers-price]')) {
      if (this.isDesktop) {
        this.submitSoon(600, () => {
          const active = document.activeElement;
          if (active && active.matches && active.matches('[data-chillers-price]')) {
            this.pendingFocus = {
              id: active.id,
              start: active.selectionStart,
              end: active.selectionEnd,
              value: active.value,
            };
          } else {
            this.pendingFocus = null;
          }
        });
      }
      return;
    }

    if (target.matches('[data-chillers-sort]')) {
      this.submitSoon(0);
    }
  }

  onSubmit(event) {
    event.preventDefault();
    const scroll = !this.isDesktop;
    this.closeDrawer({ discard: false });
    this.render(this.searchParams(), { push: true, scroll });
  }

  onClick(event) {
    if (event.target.closest('[data-chillers-open]')) {
      event.preventDefault();
      this.openDrawer();
      return;
    }

    if (event.target.closest('[data-chillers-dismiss]')) {
      event.preventDefault();
      this.closeDrawer({ discard: true });
      return;
    }

    const remove = event.target.closest('[data-chillers-remove]');
    if (remove) {
      event.preventDefault();
      this.closeDrawer({ discard: false });
      const url = new URL(remove.href, window.location.origin);
      this.render(url.search.replace(/^\?/, ''), { push: true, scroll: !this.isDesktop });
      return;
    }

    const pageLink = event.target.closest('[data-chillers-grid] .pagination a');
    if (pageLink) {
      event.preventDefault();
      const url = new URL(pageLink.href, window.location.origin);
      this.render(url.search.replace(/^\?/, ''), { push: true, scroll: true });
    }
  }

  onKeydown(event) {
    if (!this.root.classList.contains('is-drawer-open')) return;

    if (event.key === 'Escape') {
      this.closeDrawer({ discard: true });
      return;
    }

    if (event.key !== 'Tab') return;

    const drawer = this.drawer;
    if (!drawer) return;

    const focusable = Array.from(
      drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.disabled);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  openDrawer() {
    const drawer = this.drawer;
    if (!drawer || this.isDesktop) return;

    const body = this.root.querySelector('[data-chillers-filters-body]');
    this.filtersSnapshot = body ? body.innerHTML : '';
    this.lastFocus = document.activeElement;

    this.root.classList.add('is-drawer-open');
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    document.body.classList.add('chillers-filters-lock');
    this.root.querySelector('[data-chillers-open]')?.setAttribute('aria-expanded', 'true');

    const closeButton = drawer.querySelector('[data-chillers-dismiss]');
    if (closeButton) closeButton.focus();
  }

  closeDrawer({ discard = false } = {}) {
    const wasOpen = this.root.classList.contains('is-drawer-open');
    this.root.classList.remove('is-drawer-open');
    document.body.classList.remove('chillers-filters-lock');
    this.root.querySelector('[data-chillers-open]')?.setAttribute('aria-expanded', 'false');

    const drawer = this.drawer;
    if (drawer && !this.isDesktop) {
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
    }

    if (discard && wasOpen && this.filtersSnapshot) {
      const body = this.root.querySelector('[data-chillers-filters-body]');
      if (body) body.innerHTML = this.filtersSnapshot;
    }

    if (wasOpen && this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  }

  submitSoon(delay, before) {
    window.clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      if (typeof before === 'function') before();
      this.render(this.searchParams(), { push: true, scroll: false });
    }, delay);
  }

  searchParams() {
    const params = new URLSearchParams(new FormData(this.form));
    Array.from(params.entries()).forEach(([key, value]) => {
      if (value.trim() === '') params.delete(key);
    });
    return params.toString();
  }

  renderFromLocation() {
    this.render(window.location.search.replace(/^\?/, ''), { push: false, scroll: false });
  }

  render(searchParams, { push = true, scroll = false } = {}) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const token = this.controller;

    const query = searchParams
      ? `${searchParams}&section_id=${encodeURIComponent(this.sectionId)}`
      : `section_id=${encodeURIComponent(this.sectionId)}`;
    const url = `${window.location.pathname}?${query}`;

    this.root.classList.add('is-loading');

    fetch(url, { signal: token.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then((html) => {
        if (this.controller !== token) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = doc.querySelector('[data-chillers-root]');
        if (!nextRoot) throw new Error('Missing collection section');

        ['[data-chillers-toolbar]', '[data-chillers-active]', '[data-chillers-filters-body]', '[data-chillers-grid]'].forEach(
          (selector) => {
            const current = this.root.querySelector(selector);
            const next = nextRoot.querySelector(selector);
            if (current && next) current.innerHTML = next.innerHTML;
          }
        );

        if (push) {
          const nextUrl = searchParams ? `${window.location.pathname}?${searchParams}` : window.location.pathname;
          window.history.pushState({ chillersSearch: searchParams }, '', nextUrl);
        }

        this.restoreFocus();
        this.decorateFromUrl();

        if (scroll) {
          const toolbar = this.root.querySelector('[data-chillers-toolbar]');
          if (toolbar) toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        window.location.assign(
          searchParams ? `${window.location.pathname}?${searchParams}` : window.location.pathname
        );
      })
      .finally(() => {
        if (this.controller === token) this.root.classList.remove('is-loading');
      });
  }

  decorateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const categories = Array.from(this.root.querySelectorAll('[data-chillers-category]'));
    let categoryCount = 0;

    categories.forEach((input) => {
      if (params.getAll(input.name).includes(input.value)) {
        input.checked = true;
        categoryCount += 1;
      }
    });

    const allProducts = this.root.querySelector('[data-chillers-all]');
    if (allProducts && categoryCount > 0) allProducts.checked = false;

    let priceActive = false;
    const priceInputs = Array.from(this.root.querySelectorAll('[data-chillers-price]'));
    priceInputs.forEach((input) => {
      const value = params.get(input.name);
      if (!value) return;
      priceActive = true;
      if (!input.value) input.value = value;
    });

    const total = categoryCount + (priceActive ? 1 : 0);
    const toggle = this.root.querySelector('[data-chillers-open]');
    if (toggle && total > 0 && !toggle.querySelector('.chillers-filter-toggle__count')) {
      const badge = document.createElement('span');
      badge.className = 'chillers-filter-toggle__count';
      badge.textContent = String(total);
      toggle.appendChild(badge);
    }

    const active = this.root.querySelector('[data-chillers-active]');
    if (!active || !active.classList.contains('chillers-active--empty') || total === 0) return;

    const list = document.createElement('ul');
    list.className = 'chillers-active__list';

    categories.forEach((input) => {
      if (!input.checked) return;
      const label = input.closest('.chillers-option')?.querySelector('.chillers-option__label')?.textContent?.trim() || input.value;
      list.appendChild(this.createChip(label, this.urlWithout(params, [{ name: input.name, value: input.value }])));
    });

    if (priceActive) {
      const removals = priceInputs
        .filter((input) => params.get(input.name))
        .map((input) => ({ name: input.name, value: params.get(input.name) }));
      list.appendChild(this.createChip('Price', this.urlWithout(params, removals)));
    }

    const clearParams = new URLSearchParams(params.toString());
    Array.from(clearParams.keys()).forEach((key) => {
      if (key.indexOf('filter.') === 0) clearParams.delete(key);
    });
    const clearQuery = clearParams.toString();
    const clearLink = document.createElement('a');
    clearLink.className = 'chillers-active__clear';
    clearLink.href = clearQuery ? `${window.location.pathname}?${clearQuery}` : window.location.pathname;
    clearLink.setAttribute('data-chillers-remove', '');
    clearLink.textContent = 'Clear all';
    const clearItem = document.createElement('li');
    clearItem.appendChild(clearLink);
    list.appendChild(clearItem);

    active.classList.remove('chillers-active--empty');
    active.appendChild(list);
  }

  createChip(label, href) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.className = 'chillers-active__chip';
    link.href = href;
    link.setAttribute('data-chillers-remove', '');

    const text = document.createElement('span');
    text.textContent = label;
    link.appendChild(text);

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'chillers-active__icon');
    icon.setAttribute('viewBox', '0 0 12 12');
    icon.setAttribute('width', '10');
    icon.setAttribute('height', '10');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M2 2l8 8M10 2L2 10');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    icon.appendChild(path);
    link.appendChild(icon);

    const hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = 'Remove filter';
    link.appendChild(hidden);

    item.appendChild(link);
    return item;
  }

  urlWithout(params, removals) {
    const next = new URLSearchParams(params.toString());
    removals.forEach(({ name, value }) => {
      const remaining = next.getAll(name).filter((entry) => entry !== value);
      next.delete(name);
      remaining.forEach((entry) => next.append(name, entry));
    });
    const query = next.toString();
    return query ? `${window.location.pathname}?${query}` : window.location.pathname;
  }

  restoreFocus() {
    if (!this.pendingFocus) return;
    const pending = this.pendingFocus;
    this.pendingFocus = null;
    const field = this.root.querySelector(`#${CSS.escape(pending.id)}`);
    if (!field) return;
    field.value = pending.value;
    field.focus();
    if (pending.start != null && field.setSelectionRange) {
      field.setSelectionRange(pending.start, pending.end);
    }
  }
}

function bootChillersFilters(scope) {
  const roots = (scope || document).querySelectorAll('[data-chillers-root]');
  roots.forEach((root) => {
    if (root.chillersFilters) return;
    root.chillersFilters = new ChillersFilters(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootChillersFilters(document));
} else {
  bootChillersFilters(document);
}

document.addEventListener('shopify:section:load', (event) => {
  bootChillersFilters(event.target);
});

if (!window.__chillersFiltersPopstate) {
  window.__chillersFiltersPopstate = true;
  window.addEventListener('popstate', () => {
    document.querySelectorAll('[data-chillers-root]').forEach((root) => {
      if (root.chillersFilters) root.chillersFilters.renderFromLocation();
    });
  });
  document.addEventListener('keydown', (event) => {
    const root = document.querySelector('[data-chillers-root].is-drawer-open');
    if (root && root.chillersFilters) root.chillersFilters.onKeydown(event);
  });
}
